import { useEffect, useMemo, useState } from 'react';
import { MAX_RARITY, Roulette } from '~/components/roulette';
import { Tabs, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { getCaseItems, getCases } from '~/lib/api';
import type { CaseItem, CaseSummary, ItemRarity } from '~/lib/contracts';
import './index.css';

export function App() {
    const [casesData, setCasesData] = useState<CaseSummary[]>([]);
    const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
    const [activeItems, setActiveItems] = useState<CaseItem[]>([]);
    const [isCasesLoading, setIsCasesLoading] = useState(true);
    const [isItemsLoading, setIsItemsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        const loadCases = async () => {
            try {
                const nextCasesData = await getCases();
                setCasesData(nextCasesData);
                const caseParam = new URLSearchParams(window.location.search).get('case');
                const requestedCaseId = caseParam ? Number.parseInt(caseParam, 10) : Number.NaN;
                const hasRequestedCase = nextCasesData.some((caseData) => caseData.id === requestedCaseId);
                setSelectedCaseId(hasRequestedCase ? requestedCaseId : (nextCasesData[0]?.id ?? null));
                setErrorMessage(null);
            } catch {
                setErrorMessage('Failed to load cases');
            } finally {
                setIsCasesLoading(false);
            }
        };

        void loadCases();
    }, []);

    useEffect(() => {
        if (!selectedCaseId) {
            setActiveItems([]);
            return;
        }

        const loadCaseItems = async () => {
            try {
                setIsItemsLoading(true);
                const response = await getCaseItems(selectedCaseId);
                setActiveItems(response.items.map(item => ({
                    ...item,
                    rarity: Math.min(item.imageUrl ? item.rarity + 1 : item.rarity, MAX_RARITY) as ItemRarity,
                })));
                setErrorMessage(null);
            } catch {
                setActiveItems([]);
                setErrorMessage('Failed to load case items');
            } finally {
                setIsItemsLoading(false);
            }
        };

        void loadCaseItems();
    }, [selectedCaseId]);

    const selectedCaseValue = useMemo(() => {
        if (!selectedCaseId) {
            return '';
        }

        return String(selectedCaseId);
    }, [selectedCaseId]);

    return (
        <main className="mx-auto flex min-h-screen w-full flex-col justify-center p-4 max-w-screen relative">
            <div className="absolute top-[15vh] inset-x-0 flex flex-col items-center gap-6">
                <h1 className="text-4xl text-center">Russian Roulette</h1>
                <div className="mb-8 flex justify-center">
                    {isCasesLoading && (
                        <p className="text-sm text-neutral-300">Loading cases...</p>
                    )}
                    {!isCasesLoading && casesData.length === 0 ? (
                        <p className="text-sm text-neutral-300">No cases found</p>
                    ) : (
                        <Tabs
                            value={selectedCaseValue}
                            onValueChange={(value) => {
                                const nextCaseId = Number.parseInt(value, 10);
                                if (!Number.isInteger(nextCaseId)) {
                                    return;
                                }

                                setSelectedCaseId(nextCaseId);
                            }}
                        >
                            <TabsList>
                                {casesData.map((caseData) => (
                                    <TabsTrigger key={caseData.id} value={String(caseData.id)}>
                                        {caseData.name}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                        </Tabs>
                    )}
                </div>
            </div>
            <div className="-mx-4">
                {errorMessage && (
                    <p className="mb-4 text-center text-sm text-red-300">{errorMessage}</p>
                )}
                <Roulette items={isItemsLoading ? [] : activeItems} />
            </div>
        </main>
    );
}
